import { useMemo } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { T, Empty, SFG_COLOR, FG_COLOR } from "@/components/exec/ExecParts";

const kgTooltip = (v, name) => [`${(v / 1000).toFixed(2)} t (${Number(v).toFixed(1)} kg)`, name];

// Purchase: suppliers × grades pivot of this month's RM intake.
export const IntakePivot = ({ rows }) => {
  const { suppliers, grades, cell } = useMemo(() => {
    const s = [...new Set(rows.map((r) => r.supplier))];
    const g = [...new Set(rows.map((r) => r.grade))].sort();
    const c = Object.fromEntries(rows.map((r) => [`${r.supplier}|${r.grade}`, r]));
    return { suppliers: s, grades: g, cell: c };
  }, [rows]);
  if (!rows.length) return <Empty testId="intake-empty" text="No RM received yet this month." />;
  const supTotal = (s) => rows.filter((r) => r.supplier === s).reduce((a, r) => a + r.kg, 0);
  const gradeTotal = (g) => rows.filter((r) => r.grade === g).reduce((a, r) => a + r.kg, 0);
  return (
    <div className="overflow-x-auto">
      <table data-testid="intake-pivot" className="w-full text-sm">
        <thead>
          <tr className="text-[10px] tracking-[0.2em] uppercase text-slate-500">
            <th className="text-left font-semibold py-2 pr-4">Supplier</th>
            {grades.map((g) => <th key={g} className="text-right font-semibold py-2 px-3 whitespace-nowrap">{g}</th>)}
            <th className="text-right font-semibold py-2 pl-3">Total</th>
          </tr>
        </thead>
        <tbody>
          {suppliers.map((s) => (
            <tr key={s} data-testid={`intake-row-${s}`} className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
              <td className="py-2 pr-4 font-medium text-slate-800">{s}</td>
              {grades.map((g) => {
                const c = cell[`${s}|${g}`];
                return (
                  <td key={g} className="py-2 px-3 text-right whitespace-nowrap">
                    {c ? <><T kg={c.kg} className="text-slate-900" /><span className="text-[10px] text-slate-400 ml-1">· {c.coils}</span></> : <span className="text-slate-300">—</span>}
                  </td>
                );
              })}
              <td className="py-2 pl-3 text-right"><T kg={supTotal(s)} className="font-bold text-slate-900" /></td>
            </tr>
          ))}
          <tr className="border-t-2 border-slate-900">
            <td className="py-2 pr-4 text-[10px] tracking-[0.2em] uppercase font-bold text-slate-900">Grade total</td>
            {grades.map((g) => <td key={g} className="py-2 px-3 text-right"><T kg={gradeTotal(g)} className="font-bold" /></td>)}
            <td className="py-2 pl-3 text-right"><T kg={rows.reduce((a, r) => a + r.kg, 0)} className="font-black text-slate-900" /></td>
          </tr>
        </tbody>
      </table>
      <p className="text-[10px] text-slate-400 mt-2">Cell = tonnes · number of coils</p>
    </div>
  );
};

export const MonthlyChart = ({ data }) => (
  <div data-testid="monthly-sales-chart" className="h-72">
    <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 800, height: 288 }}>
      <BarChart data={data.map((m) => ({ ...m, SFG: m.sfg_kg, FG: m.fg_kg }))} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
        <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(1)}t`} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} width={48} />
        <Tooltip formatter={kgTooltip} cursor={{ fill: "#f1f5f9" }} contentStyle={{ borderRadius: 2, fontSize: 12 }} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="SFG" stackId="s" fill={SFG_COLOR} />
        <Bar dataKey="FG" stackId="s" fill={FG_COLOR} radius={[2, 2, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

export const QuarterTiles = ({ quarters }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
    {quarters.map((q) => (
      <div key={q.quarter} data-testid={`quarter-${q.quarter}`}
        className={`border rounded-sm p-4 transition-transform duration-150 hover:-translate-y-0.5 ${q.is_future ? "border-dashed border-slate-200 opacity-50" : "border-slate-200"}`}>
        <div className="flex items-baseline justify-between">
          <p className="font-heading font-black text-slate-900">{q.quarter}</p>
          <p className="text-[10px] tracking-wider uppercase text-slate-400">{q.months}</p>
        </div>
        <T kg={q.total_kg} className="block text-2xl font-bold text-slate-900 mt-2" testId={`quarter-${q.quarter}-total`} />
        <div className="flex gap-3 text-xs mt-2">
          <span style={{ color: SFG_COLOR }}>SFG <T kg={q.sfg_kg} /></span>
          <span style={{ color: FG_COLOR }}>FG <T kg={q.fg_kg} /></span>
        </div>
      </div>
    ))}
  </div>
);

export const YearlyTable = ({ years }) => {
  if (!years.length) return <Empty testId="yearly-empty" text="No dispatches recorded yet." />;
  const max = Math.max(1, ...years.map((y) => y.total_kg));
  return (
    <ul data-testid="yearly-sales" className="space-y-3">
      {years.slice().reverse().map((y) => (
        <li key={y.fy} data-testid={`yearly-${y.fy}`}>
          <div className="flex justify-between text-sm">
            <span className="font-semibold text-slate-800">{y.fy}</span>
            <T kg={y.total_kg} className="font-bold text-slate-900" />
          </div>
          <div className="flex h-2 mt-1 rounded-sm overflow-hidden bg-slate-100" style={{ width: `${Math.max(4, (y.total_kg / max) * 100)}%` }}>
            <div style={{ width: `${y.total_kg ? (y.sfg_kg / y.total_kg) * 100 : 0}%`, background: SFG_COLOR }} />
            <div style={{ width: `${y.total_kg ? (y.fg_kg / y.total_kg) * 100 : 0}%`, background: FG_COLOR }} />
          </div>
          <p className="text-[11px] text-slate-500 mt-1">SFG <T kg={y.sfg_kg} /> · FG <T kg={y.fg_kg} /></p>
        </li>
      ))}
    </ul>
  );
};

export const RmStockTable = ({ rows }) => {
  if (!rows.length) return <Empty testId="rm-stock-empty" text="No unplanned RM in stock." />;
  const max = Math.max(1, ...rows.map((r) => r.kg));
  return (
    <table data-testid="rm-stock-table" className="w-full text-sm">
      <thead>
        <tr className="text-[10px] tracking-[0.2em] uppercase text-slate-500">
          <th className="text-left font-semibold py-2">Grade</th>
          <th className="text-right font-semibold py-2 px-3">Coils</th>
          <th className="text-right font-semibold py-2">Weight</th>
          <th className="w-1/3 py-2" />
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.grade} data-testid={`rm-stock-${r.grade}`} className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
            <td className="py-2 font-medium text-slate-800">{r.grade}</td>
            <td className="py-2 px-3 text-right font-mono-data">{r.coils}</td>
            <td className="py-2 text-right"><T kg={r.kg} className="font-semibold text-slate-900" /></td>
            <td className="py-2 pl-4">
              <div className="h-1.5 bg-slate-100 rounded-sm overflow-hidden">
                <div className="h-full bg-slate-900 rounded-sm" style={{ width: `${(r.kg / max) * 100}%` }} />
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
